import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { AlertTriangle, Check, Dot, IndianRupee, Sparkles } from "lucide-react";
import Reveal from "@/components/landing/Reveal";

// Four numbered manifesto chapters. This is the LAYOUT scaffold only —
// the real business-problem storytelling content will be added later.
const CHAPTERS = [
  { no: "01", label: "AI Product Import" },
  { no: "02", label: "Multiple Software Subscriptions" },
  { no: "03", label: "Customer Follow-ups" },
  { no: "04", label: "Inventory Decisions" },
];

const SkeletonBlock = () => (
  <div className="space-y-3.5" aria-hidden="true">
    <div className="h-3 w-1/3 rounded-full bg-[#f4f4f5]" />
    <div className="h-3 w-full rounded-full bg-[#f4f4f5]" />
    <div className="h-3 w-11/12 rounded-full bg-[#f4f4f5]" />
    <div className="h-3 w-3/4 rounded-full bg-[#f4f4f5]" />
  </div>
);

// Real content for the Business Impact sticky cards.
const SubHeading = ({ children }) => (
  <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#a1a1aa]">
    {children}
  </p>
);

const CARD_CONTENT = {
  0: {
    beforeTitle: "Before AMRA AI",
    afterTitle: "After AMRA AI",
    problem: [
      "Supplier Excel contains ~2,000 products",
      "Manual verification or ₹1,500 paid to software vendors every import",
      "Happens around 6 times every year",
    ],
    impactBefore: "₹1,500 × 6 = ₹9,000 recurring yearly expense",
    solutionLabel: "AI Solution",
    solution: [
      "AI scans the Excel automatically",
      "Detects missing fields & duplicates",
      "Suggests corrections before import",
      "Import completed safely in minutes",
    ],
    impactAfter: [
      "₹9,000 yearly cost eliminated",
      "Hours of manual work saved",
      "One-time software investment → Continuous yearly savings",
    ],
  },
  1: {
    beforeTitle: "Before AMRA AI Business OS",
    afterTitle: "After AMRA AI Business OS",
    problem: [
      "A billing software installed on 5 business systems",
      "Annual subscription charged separately for every system",
      "Only 1 system is used throughout the year",
      "Remaining 4 systems are used mainly during the seasonal business months (Sept – Dec)",
    ],
    impactBefore:
      "₹2,400 (8 months unused) × 4 systems = ₹9,500 recurring yearly software expense paid without using software",
    solutionLabel: "Solution",
    solution: [
      "One integrated business platform",
      "Billing, Products, Customers, Reports & Statements together",
      "Single system with one shared database",
      "One-time software purchase",
    ],
    impactAfter: [
      "Multiple subscriptions eliminated",
      "One integrated platform",
      "Lower long-term software cost",
    ],
  },
  2: {
    beforeTitle: "Before AMRA AI Business OS",
    afterTitle: "After AMRA AI Business OS",
    problem: [
      "Customer follow-ups depended on employee memory",
      "Pending payments were often overlooked",
      "Seasonal customers were not contacted at the right time",
      "No visibility into inactive customers",
    ],
    impactBefore:
      "Missed follow-ups led to delayed collections and lost repeat business whose real impact cannot be assessed exactly.",
    impactBeforeVariant: "warning",
    solutionLabel: "Solution",
    solution: [
      "Centralized customer due tracking",
      "Timely payment reminders",
      "Seasonal customer follow-up alerts",
      "Easy identification of inactive customers",
    ],
    impactAfter: [
      "Consistent customer follow-ups",
      "Better collection of outstanding dues",
      "Improved repeat business opportunities",
    ],
  },
  3: {
    beforeTitle: "Before AMRA AI Business OS",
    afterTitle: "After AMRA AI Business OS",
    problem: [
      "Inventory decisions relied on experience and guesswork",
      "No visibility into top-selling products",
      "Dead stock often remained unnoticed",
      "Fast-moving products were reordered too late",
    ],
    impactBefore:
      "Missed sales opportunities and unnecessary inventory investment",
    impactBeforeVariant: "warning",
    solutionLabel: "Solution",
    solution: [
      "Clear visibility into sales trends",
      "Identify top-selling and dead stock instantly",
      "Timely reorder recommendations",
      "Better inventory planning with business insights",
    ],
    impactAfter: [
      "Smarter inventory decisions",
      "Reduced dead stock",
      "Improved product availability and revenue opportunities",
    ],
  },
};

const CardContent = ({ data }) => (
  <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
    {/* Before */}
    <div className="rounded-xl border border-black/[0.06] bg-[#fafafa] p-6">
      <h4 className="font-heading text-lg font-bold tracking-tight text-[#0a0a0a]">
        {data.beforeTitle}
      </h4>

      <div className="mt-5">
        <SubHeading>Problem</SubHeading>
        <ul className="mt-3 space-y-2.5">
          {data.problem.map((line, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-sm leading-relaxed text-[#52525b]"
            >
              <Dot className="mt-0.5 h-4 w-4 shrink-0 text-[#a1a1aa]" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6">
        <SubHeading>Business Impact</SubHeading>
        <div className="mt-3 flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          {data.impactBeforeVariant === "warning" ? (
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
          ) : (
            <IndianRupee className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
          )}
          <span className="text-sm font-semibold text-red-600">
            {data.impactBefore}
          </span>
        </div>
      </div>
    </div>

    {/* After */}
    <div className="rounded-xl border border-brand-teal/20 bg-brand-teal/[0.04] p-6">
      <h4 className="font-heading text-lg font-bold tracking-tight text-[#0a0a0a]">
        {data.afterTitle}
      </h4>

      <div className="mt-5">
        <SubHeading>{data.solutionLabel}</SubHeading>
        <ul className="mt-3 space-y-2.5">
          {data.solution.map((line, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-sm leading-relaxed text-[#0a0a0a]"
            >
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-teal" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6">
        <SubHeading>Business Impact</SubHeading>
        <ul className="mt-3 space-y-2">
          {data.impactAfter.map((line, i) => (
            <li
              key={i}
              className="flex items-center gap-2.5 rounded-lg border border-brand-emerald/25 bg-brand-emerald/[0.07] px-4 py-2.5 text-sm font-semibold text-emerald-700"
            >
              <Sparkles className="h-4 w-4 shrink-0 text-brand-emerald" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

export const BusinessImpact = () => {
  const [active, setActive] = useState(0);
  const refs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.dataset.index);
            setActive(idx);
          }
        });
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );
    refs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="business-impact"
      data-testid="business-impact-section"
      className="relative scroll-mt-20 px-6 py-24 lg:px-10 lg:py-32"
    >
      <div className="mx-auto max-w-7xl">
        <Reveal className="max-w-2xl">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-teal">
            Business Impact
          </span>
          <h2 className="mt-4 font-heading text-4xl font-bold tracking-tight text-[#0a0a0a] sm:text-5xl">
            Built from the inside out.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-[#52525b] sm:text-lg">
            Four real operational problems from AMRA Decorations — and how the
            system came to solve each one. The full story arrives here soon.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-12 lg:grid-cols-12">
          {/* Sticky chapter index */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <ul className="space-y-1">
                {CHAPTERS.map((c, i) => (
                  <li key={c.no}>
                    <div
                      data-testid={`chapter-index-${c.no}`}
                      className={`flex items-center gap-4 rounded-xl px-4 py-3 transition-colors duration-300 ${
                        active === i ? "bg-[#fafafa]" : "bg-transparent"
                      }`}
                    >
                      <span
                        className={`font-mono text-sm transition-colors duration-300 ${
                          active === i ? "text-brand-teal" : "text-[#a1a1aa]"
                        }`}
                      >
                        {c.no}
                      </span>
                      <span
                        className={`font-heading text-lg font-medium transition-colors duration-300 ${
                          active === i ? "text-[#0a0a0a]" : "text-[#a1a1aa]"
                        }`}
                      >
                        {c.label}
                      </span>
                      {active === i && (
                        <motion.span
                          layoutId="chapter-active"
                          className="ml-auto h-1.5 w-1.5 rounded-full bg-brand-emerald"
                        />
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Chapter panels (layout scaffold) */}
          <div className="space-y-6 lg:col-span-8">
            {CHAPTERS.map((c, i) => (
              <div
                key={c.no}
                data-index={i}
                data-testid={`chapter-panel-${c.no}`}
                ref={(el) => (refs.current[i] = el)}
                className="rounded-2xl border border-black/[0.07] bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.03)] sm:p-10"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-7xl font-bold leading-none text-[#f4f4f5]">
                    {c.no}
                  </span>
                  {!CARD_CONTENT[i] && (
                    <span className="rounded-full border border-black/[0.08] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-[#a1a1aa]">
                      Story coming soon
                    </span>
                  )}
                </div>
                <h3 className="mt-6 font-heading text-2xl font-bold tracking-tight text-[#0a0a0a]">
                  {c.label}
                </h3>
                {CARD_CONTENT[i] ? (
                  <CardContent data={CARD_CONTENT[i]} />
                ) : (
                  <div className="mt-6">
                    <SkeletonBlock />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BusinessImpact;
